import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { ApplicationContextStore } from '@raptor-fe/services';
import { PermissionKey } from '@raptor-fe/core';

/**
 * Structural directive that renders its host element only when the current user holds the required
 * permission key(s). Bind a single key for an exact check, or an array for any-of semantics (the
 * host renders when the user holds at least one of the keys). Admin and system roles project to the
 * full catalog server-side, so privileged users always satisfy the check.
 *
 * @example Single key
 * ```html
 * <button *libHasPermission="Permissions.Customer.Create">New customer</button>
 * ```
 *
 * @example Any-of
 * ```html
 * <section *libHasPermission="[Permissions.Quote.View, Permissions.WorkOrder.View]">...</section>
 * ```
 */
@Directive({
    selector: '[libHasPermission]',
})
export class HasPermissionDirective {
    private readonly templateRef = inject(TemplateRef<unknown>);
    private readonly viewContainer = inject(ViewContainerRef);
    private readonly applicationContextStore = inject(ApplicationContextStore);
    private isRendered = false;

    /**
     * The permission key, or any-of array of keys, that gates rendering of the host element.
     */
    readonly libHasPermission = input.required<PermissionKey | string | Array<PermissionKey | string>>();

    constructor() {
        effect(() => {
            const required = this.libHasPermission();
            const requiredKeys = Array.isArray(required) ? required : [required];
            this.render(this.applicationContextStore.hasAnyPermission(requiredKeys));
        });
    }

    /**
     * Attaches or detaches the embedded view to match the granted state, without re-creating a view
     * that is already in the correct state.
     *
     * @param granted Whether the user holds the required permission(s).
     */
    private render(granted: boolean): void {
        if (granted && !this.isRendered) {
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.isRendered = true;
        } else if (!granted && this.isRendered) {
            this.viewContainer.clear();
            this.isRendered = false;
        }
    }
}
